/**
 * Radius (px) for block-trade diamond markers on historical and intraday charts.
 * Most prints share a similar base size; only the largest notionals in the visible
 * window grow meaningfully larger (power curve on midrank compresses the mid-pack).
 */

/** Typical diamond size — most visible trades land near here. */
const RADIUS_BASE_PX = 6.5;
/** Reserved for the largest prints in the current chart window. */
const RADIUS_MAX_PX = 11.5;
/** >1 pulls mid-ranks toward base; top ~5–10% expand toward max. */
const SIZE_RANK_GAMMA = 2.75;

export function blockTradeNotionalUsd(trade: {
  dollar_value?: number;
  size?: number;
  price?: number;
}): number {
  const dv = Number(trade.dollar_value);
  if (Number.isFinite(dv) && dv > 0) return dv;
  const size = Math.max(Number(trade.size) || 0, 0);
  const price = Number(trade.price) || 0;
  if (size > 0 && price > 0) return size * price;
  return size;
}

function radiusFromNotionalRank(t: number): number {
  const clamped = Math.min(Math.max(t, 0), 1);
  const emphasis = Math.pow(clamped, SIZE_RANK_GAMMA);
  return RADIUS_BASE_PX + emphasis * (RADIUS_MAX_PX - RADIUS_BASE_PX);
}

/**
 * Monotonic radius in trade notional among `rows`, with ties sharing the same radius.
 * Mid-pack sizes stay near `RADIUS_BASE_PX`; only top ranks approach `RADIUS_MAX_PX`.
 */
export function blockTradeDiamondRadiusByKey(
  rows: Array<{ key: string; notional: number }>,
): Map<string, number> {
  const out = new Map<string, number>();
  const valid = rows.filter((r) => r.notional > 0);
  const n = valid.length;
  if (n === 0) return out;

  const sorted = [...valid].sort((a, b) => {
    const d = a.notional - b.notional;
    if (d !== 0) return d;
    return a.key.localeCompare(b.key);
  });

  if (n === 1) {
    out.set(sorted[0].key, RADIUS_BASE_PX);
    return out;
  }

  let i = 0;
  while (i < n) {
    let j = i + 1;
    while (j < n && sorted[j].notional === sorted[i].notional) {
      j += 1;
    }
    const lo = i;
    const hi = j - 1;
    const avgRank = (lo + hi) / 2;
    const t = avgRank / (n - 1);
    const r = radiusFromNotionalRank(t);
    for (let k = i; k < j; k += 1) {
      out.set(sorted[k].key, r);
    }
    i = j;
  }

  return out;
}

/** Fallback when a marker is not in the visible ranking set (single trade, popover-only, etc.). */
export function blockTradeDiamondRadiusFallback(trade: {
  dollar_value?: number;
  size?: number;
  price?: number;
}): number {
  const notional = blockTradeNotionalUsd(trade);
  const ref = 50_000_000;
  const logSpan = Math.log10(Math.max(notional / ref, 1));
  const emphasis = Math.min(Math.pow(logSpan / 3, SIZE_RANK_GAMMA), 1);
  return radiusFromNotionalRank(emphasis);
}
